<template>
    <app-layout title="Settings">
        <page-header>
            <template #title>Settings - Delivery Zones</template>
        </page-header>

        <settings-navigation />

        <div class="px-8 gap-4 flex">
            <div class="flex flex-col w-2/3 border">
                <div class="flex flex-row items-center gap-4 h-10 pl-2">
                    <span class="h-7 font-bold">Delivery Stats:</span>
                    <jet-tertiary-button class="py-1 h-7"
                        :style="{
                            backgroundColor: deliveryStatsType === 'failed' ? '#6c757d' : undefined,
                            color: deliveryStatsType === 'failed' ? 'white' : undefined,
                        }"
                        @click="deliveryStatsType = 'failed'"
                    >
                        Out of Bounds
                    </jet-tertiary-button>
                    <jet-tertiary-button class="py-1 h-7"
                        :style="{
                            backgroundColor: deliveryStatsType === 'all' ? '#6c757d' : undefined,
                            color: deliveryStatsType === 'all' ? 'white' : undefined,
                        }"
                        @click="deliveryStatsType = 'all'"
                    >
                        Completed
                    </jet-tertiary-button>
                    <template v-if="deliveryStatsType">
                        <datepicker
                            inputClassName="dp-custom-input h-7"
                            v-model="dateRange"
                            :is24="false"
                            :format="formatDateRange"
                            :enableTimePicker="false"
                            :autoApply="true"
                            range
                            twoCalendars
                            placeholder="Select Date Range"
                            weekStart=0
                        />
                        <jet-button type="button" class="h-7" @click="getDeliveries()">
                            Apply
                        </jet-button>
                        <jet-tertiary-button class="py-1 h-7" @click="clearDeliveries()">
                            Reset
                        </jet-tertiary-button>
                    </template>
                </div>

                <GoogleMap
                    api-key="AIzaSyD6q8VzUkUq6Stn2xgZrk1WhJjCAmkssFs"
                    ref="googleMap"
                    class="w-full h-full"
                    :center="center"
                    :zoom="10"
                    :zoomControl="true"
                    :mapTypeControl="false"
                    :scaleControl="false"
                    :streetViewControl="false"
                    :rotateControl="false"
                    :fullscreenControl="false"
                    :disableDefaultUi="false"
                    @click="resetSelection()"
                >
                    <Marker
                        v-for="(m, index) in markers"
                        :key="'marker' + index"
                        :options="{
                            position: m.position,
                            draggable: true,
                        }"
                        @dragend="updateMarkerPos($event.latLng, index)"
                        @dblclick="drawCircle()"
                    />

                    <Marker
                        v-for="(m, index) in failed_requests"
                        :key="'fail' + index"
                        :options="{
                            position: m.position,
                            title: m.title,
                        }"
                    >
                        <InfoWindow>
                            <table>
                                <tbody>
                                    <tr>
                                        <td class="font-bold align-top">Address:</td><td v-html="m.address"></td>
                                    </tr>
                                    <tr v-if="m.name">
                                        <td class="font-bold">Name:</td><td v-html="m.name"></td>
                                    </tr>
                                    <tr v-if="m.email">
                                        <td class="font-bold">Email:</td><td v-html="m.email"></td>
                                    </tr>
                                    <tr v-if="m.phone">
                                        <td class="font-bold">Phone:</td><td v-html="m.phone"></td>
                                    </tr>
                                    <tr>
                                        <td class="font-bold">Date:</td><td v-html="m.date"></td>
                                    </tr>
                                </tbody>
                            </table>
                        </InfoWindow>
                    </Marker>

                    <Marker
                        v-for="(m, index) in all_deliveries"
                        :key="'del' + index"
                        :options="{
                            position: m.position,
                            icon: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png'
                        }"
                    />

                    <Circle
                        v-for="(pin, index) in all_areas"
                        ref="circle"
                        :key="'circle' + index"
                        :options="pin"
                        @click="selectCircle(pin.id, index)"
                        @dragend="changesMade()"
                        @drag="circleCenterChanged(pin.id, index)"
                        @center_changed="circleCenterChanged(pin.id, index)"
                        @radius_changed="circleRadiusChanged(pin.id, index)"
                    />
                </GoogleMap>
            </div>

            <div class="flex w-1/3 border">
                <div class="flex flex-col w-1/2 border-r">
                    <div class="flex justify-between items-center h-10 pl-2 pr-2">
                        <span class="h-7 font-bold">Zones</span>
                        <jet-button type="button" class="h-7" :disabled="this.changes_made" @click="addZone()">
                            Add
                        </jet-button>
                    </div>
                    <div class="zone-list">
                        <div v-for="zone in sortedZones" :key="zone.id" class="zone"
                            :class="{ 'selected-zone': selected_zone?.id === zone.id }"
                            @click="selectZone(zone)"
                        >
                            <span class="zone-name">{{ zone.name }}</span>
                            <div class="zone-color" :style="{ backgroundColor: zone.colour }"></div>
                        </div>
                    </div>
                </div>

                <div class="flex flex-col w-1/2">
                    <div class="flex justify-between items-center h-10 pl-2 pr-2">
                        <span class="h-7 font-bold">Zone Detail</span>
                        <div v-if="selected_zone !== null">
                            <toggle v-model="selected_zone.active" id="active" @change="changesMade()"/>
                            <jet-label for="active" value="Active" class="mb-0 ml-2 inline-block"/>
                        </div>
                    </div>
                    <div class="zone-detail">
                        <template v-if="selected_zone !== null">
                            <div class="mb-2">
                                <jet-label for="zone-name" value="Name"/>
                                <div class="flex">
                                    <jet-input type="text" id="zone-name" ref="name"
                                        v-model="selected_zone.name"
                                        class="w-full rounded-r-none h-7"
                                        @change="changesMade()"
                                    />
                                    <span class="border border-l-0 rounded-l-none rounded-r h-7">
                                        <input type="color" name="colour" class="w-8"
                                            :value="selected_zone.colour"
                                            @input="changeZoneColor($event.target.value)"
                                        />
                                    </span>
                                </div>
                                <jet-input-error class="mt-1" :message="errors.name" />
                            </div>

                            <div class="mb-2">
                                <jet-label value="Serviced By:"/>
                                <multiselect
                                    v-model="selected_zone.location_id"
                                    :options="servicedByOptions"
                                    :canDeselect="false"
                                    :canClear="false"
                                    :classes="{ singleLabel: 'w-full px-2' }"
                                    class="h-7 multi-sm"
                                    openDirection="bottom"
                                    @change="5"
                                />
                            </div>

                            <div class="mb-2">
                                <jet-label for="delivery-fee" value="Delivery Fee:"/>
                                <div class="flex">
                                    <span class="px-3 border border-r-0 rounded-r-none rounded-l h-7"
                                    style="line-height: 26px;"
                                    >$</span>
                                    <jet-input v-model="selected_zone.fee" type="number" class="w-full rounded-l-none h-7" id="delivery-fee"/>
                                </div>
                                <jet-input-error class="mt-1" :message="errors.fee"/>

                                <div class="flex justify-end items-center mt-1">
                                    <jet-input id="free-over" type="checkbox" class="h-4 w-4"
                                        :checked="selected_zone.free_delivery_available"
                                        @change="selected_zone.free_delivery_available = !selected_zone.free_delivery_available"
                                    />
                                    <jet-label for="free-over" value="Free over $85" class="ml-1 inline mb-0"/>
                                </div>
                            </div>

                            <div class="mb-2">
                                <jet-label for="daily-limit" value="Daily Limit:"/>
                                <jet-input v-model="selected_zone.limit" type="number" class="w-full h-7" id="daily-limit"/>
                                <jet-input-error class="mt-1" :message="errors.limit"/>
                            </div>

                            <div v-for="(group_day, index) in selected_zone.group_delivery_days" :key="index" class="border rounded p-2 mb-2">
                                <jet-label value="Delivery Days:" />
                                <multiselect
                                    v-model="group_day.days"
                                    :options="getListOfAvailableDays(group_day)"
                                    :closeOnSelect="false"
                                    :canClear="false"
                                    class="multi-lg text-base delivery-days"
                                    mode="tags"
                                    @change="changeAvailableDays($event)"
                                />
                                <jet-input-error class="mt-1"
                                    :message="errors[`group_delivery_days.${index}.days`]"
                                />

                                <jet-label class="mb-0" value="Delivery Times:"/>
                                <div class="flex flex-col">
                                    <div class="flex mb-1 border-b">
                                        <div class="w-2/3 text-sm">Label</div>
                                        <div class="w-1/3 text-sm">Active</div>
                                    </div>
                                    <div v-for="(slot, indexTime) in group_day.times" :key="slot.id" class="flex mb-1">
                                        <div class="w-2/3 text-sm">
                                            {{ slot.label ? slot.label : `${slot.from} - ${slot.to}` }}
                                        </div>
                                        <div class="w-1/3 flex items-center">
                                            <div class="w-3/4 text-sm">
                                                <toggle class="delivery-toggle" v-model="slot.pivot.is_enable" @change="changesMade()"/>
                                            </div>
                                            <font-awesome-icon class="w-1/4 cursor-pointer" :icon="['fas', 'trash']"
                                                @click="deleteTimeSlotFromDeliveryZone(group_day, indexTime, slot)"
                                            />
                                        </div>
                                    </div>

                                    <div v-if="!group_day.addTimeSlot" class="flex justify-end mb-1">
                                        <div class="w-1/3 h-5 flex justify-end items-center">
                                            <font-awesome-icon :icon="['far', 'square-plus']"
                                                class="w-1/4 cursor-pointer"
                                                @click="showTimeSlots(group_day)"
                                            />
                                        </div>
                                    </div>

                                    <div v-else class="flex mb-1 items-center">
                                        <multiselect
                                            v-model="timeIndex"
                                            :options="timeOptions(group_day.availableTimes)"
                                            :canDeselect="false"
                                            :canClear="false"
                                            :classes="{ singleLabel: 'w-full px-2' }"
                                            class="w-4/5 h-7 multi-sm delivery-times-multiselect"
                                            openDirection="bottom"
                                            @change="changesMade()"
                                        />
                                        <div class="w-1/5 flex">
                                            <font-awesome-icon :icon="['fas', 'xmark']"
                                                class="w-1/2 cursor-pointer"
                                                @click="hideTimeSlots(group_day)"
                                            />
                                            <font-awesome-icon :icon="['fas', 'check']"
                                                class="w-1/2 cursor-pointer"
                                                @click="addTimeSlotToDeliveryZone(group_day)"
                                            />
                                        </div>
                                    </div>

                                    <div v-if="selected_zone.group_delivery_days.length > 1" class="border-t flex justify-center">
                                        <jet-danger-button class="mt-1 h-7" @click="deleteDeliverySlot(index)">
                                            Delete Slot
                                        </jet-danger-button>
                                    </div>
                                </div>
                            </div>

                            <div v-if="!addNewSlot && availableDays.length > 0" class="mb-2 flex justify-end items-center">
                                <jet-button type="button" class="h-7" @click="addDeliverySlot()">
                                    Add Slot
                                </jet-button>
                            </div>

                            <div class="mb-2">
                                <jet-label for="postal-codes" value="Postal Codes: (Used)" />
                                <textarea id="postal-codes" style="line-height: 1.1rem"
                                    class="resize-none block rounded w-full h-16"
                                    v-model="selected_zone.postal_codes"
                                />
                                <jet-input-error class="mt-1" />
                            </div>

                            <div class="mb-2">
                                <jet-label value="Delivery Areas: (Not used)" />
                                <div class="flex flex-col">
                                    <div class="flex mb-1 border-b">
                                        <div class="w-2/3 text-sm">Center Point</div>
                                        <div class="w-1/3 text-sm">Radius (km)</div>
                                    </div>
                                    <div v-for="area in selected_zone.areas" :key="area.id" class="flex mb-1">
                                        <div class="w-2/3 text-xs" :class="{ 'font-bold': selected_circle === area.id }">
                                            {{area.lat.toFixed(6)}} , {{area.lng.toFixed(6)}}
                                        </div>
                                        <div class="w-1/3 flex items-center gap-2">
                                            <jet-input type="number" step="0.1"
                                                class="w-3/4 h-5 text-sm delivery-area-input"
                                                :class="{ 'font-bold': selected_circle === area.id }"
                                                :value="area.radius"
                                                @input="updateRadius(area.id, $event.target.value)"
                                                @focus="changingAreaFromInput = area.id"
                                                @blur="changingAreaFromInput = null; updateRadius(area.id, $event.target.value)"
                                            />
                                            <font-awesome-icon :icon="['fas', 'trash']"
                                                :class="{ 'font-bold': selected_circle === area.id }"
                                                class="w-1/4 cursor-pointer"
                                                @click="deleteArea(area.id)"
                                            />
                                        </div>
                                    </div>
                                    <div class="flex mb-1">
                                        <div class="w-2/3 text-sm">
                                            {{ this.adding_area ? 'Adding area...' : '' }}
                                        </div>
                                        <div class="w-1/3 flex items-center gap-2">
                                            <div class="w-3/4 h-5"></div>
                                            <font-awesome-icon
                                                v-if="!this.adding_area"
                                                :icon="['far', 'square-plus']"
                                                class="w-1/4 cursor-pointer"
                                                @click="addMarker()"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="flex flex-col">
                                <div class="flex mb-2" style="justify-content: space-evenly;">
                                    <jet-button class="w-24 justify-center" @click="handleSubmit()">
                                        Save
                                    </jet-button>
                                    <jet-tertiary-button class="w-24 justify-center" @click="cancel()">
                                        Cancel
                                    </jet-tertiary-button>
                                </div>

                                <div class="flex justify-center">
                                    <jet-danger-button @click="deleteZone()">
                                        Delete Zone
                                    </jet-danger-button>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>

        <!-- Cancel Modal -->
        <jet-dialog-modal :show="showCancelModal">
            <template #title>
                <h3 class="text-2xl">Warning</h3>
            </template>

            <template #content>
                Changes have been made that are not saved! Are you sure you want to discard your changes?
            </template>

            <template #footer>
                <jet-danger-button class="mr-2" @click="confirmCancel()">
                    Yes
                </jet-danger-button>

                <jet-button class="mr-2" @click="showCancelModal = false">
                    No
                </jet-button>
            </template>
        </jet-dialog-modal>

        <!-- Delete Modal -->
        <jet-dialog-modal :show="showDeleteModal">
            <template #title>
                <h3 class="text-2xl">Warning</h3>
            </template>

            <template #content>
                Are you sure you want to delete this zone?
            </template>

            <template #footer>
                <jet-danger-button class="mr-2" @click="confirmDelete()">
                    Yes
                </jet-danger-button>

                <jet-button class="mr-2" @click="showDeleteModal = false">
                    No
                </jet-button>
            </template>
        </jet-dialog-modal>
    </app-layout>
</template>

<script>
import { DateTime } from "luxon";
import { GoogleMap, Marker, Circle, InfoWindow } from "vue3-google-map";
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetDialogModal from '@/Jetstream/DialogModal';
import SettingsNavigation from '@/Pages/Settings/Partials/SettingsNavigation';
import Multiselect from '@vueform/multiselect';
import Toggle from '@vueform/toggle';
import Datepicker from '@vuepic/vue-datepicker';

import '@vueform/multiselect/themes/default.css';
import '@vueform/toggle/themes/default.css';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
    components: {
        GoogleMap,
        Marker,
        Circle,
        InfoWindow,
        AppLayout,
        PageHeader,
        JetLabel,
        JetInput,
        JetInputError,
        JetButton,
        JetDangerButton,
        JetTertiaryButton,
        JetDialogModal,
        SettingsNavigation,
        Multiselect,
        Toggle,
        Datepicker,
    },

    props: ['startDate', 'endDate', 'zonesProps', 'deliveryTimes'],

    data() {
        return {
            servicedByOptions: [
                { value: 0, label: 'Produce Express' },
                { value: 6, label: 'Fora Ancaster' },
            ],
            zones: JSON.parse(JSON.stringify(this.zonesProps)),
            changingAreaFromInput: false,
            errors: {},

            addNewSlot: false,
            timeIndex: 0,
            center: { lat: 43.026806048083, lng: -80.50453735351562 },
            selected_zone: null,
            selected_circle: null,
            markers: [],
            failed_requests: [],
            all_deliveries: [],
            adding_area: false,
            changes_made: false,
            showCancelModal: false,
            showDeleteModal: false,
            alertMessage: null,
            alertType: null,
            deliveryStatsType: null,
            days: ['monday','tuesday','wednesday','thursday','friday','saturday'],
            dateRange: [this.startDate, this.endDate],
            new_zone: {
                active: true,
                name: '',
                location_id: 0,
                colour: '#d41534',
                limit: null,
                fee: null,
                postal_codes: '',
                free_delivery_available: false,
                group_delivery_days: [{
                    addTimeSlot: false,
                    availableTimes: [],
                    days: [],
                    times: [],
                }],
                areas: [],
            },
            all_areas: [],
        }
    },

    mounted() {
        this.setZones();
    },

    computed: {
        sortedZones() {
            let zones = this.zones;
            zones.sort((a, b) => a.name.localeCompare(b.name))

            return zones;
        },
        availableDays() {
            let days = this.days;
            this.selected_zone.group_delivery_days.forEach(group => {
                days = days.filter(day => !group.days.includes(day));
            });

            return days;
        }
    },

    methods: {
        timeOptions(list) {
            let options = [];

            for (const slotIndex in list) {
                const slot = list[slotIndex];
                options.push({ label: slot.label ? slot.label : `${slot.from} - ${slot.to}`, value: slotIndex });
            }

            return options;
        },

        formatDateRange(dateRange) {
            const start = dateRange[0];
            const end = dateRange[1];

            if (start && end) {
                let thisYear = DateTime.now().toFormat('yyyy');
                let startYear = DateTime.fromJSDate(start).toFormat('yyyy');
                let endYear = DateTime.fromJSDate(end).toFormat('yyyy');
                let startDate = startYear === thisYear ? DateTime.fromJSDate(start).toFormat('LLL. d') : DateTime.fromJSDate(start).toFormat("LLL. d, yyyy");
                let endDate = endYear === thisYear ? DateTime.fromJSDate(end).toFormat('LLL. d') : DateTime.fromJSDate(end).toFormat("LLL. d, yyyy");

                return startDate + ' - ' + endDate;
            }

            return null;
        },

        setZones() {
            let all_areas = [];
            this.zones.forEach(zone => {
                if (zone.active) {
                    zone.areas.forEach(area => {
                        let newArea = {}
                        newArea.id = area.id;
                        newArea.delivery_zone_id = area.delivery_zone_id;

                        newArea.center = { lat: area.lat, lng: area.lng };
                        newArea.radius = area.radius * 1000;
                        newArea.fillColor = zone.colour;
                        newArea.fillOpacity = 0.3;
                        newArea.strokeWeight = 1;
                        newArea.strokeColor = '#707070';
                        all_areas.push(newArea);
                    });
                }
            });

            this.all_areas = all_areas;
        },

        getListOfAvailableDays(group_day) {
            let list = JSON.parse(JSON.stringify(this.days));

            for (const item of this.selected_zone.group_delivery_days) {
                if (item === group_day) continue;

                list = _.without(list, ...item.days);
            }

            return list;
        },

        selectZone(zone) {
            if (this.changes_made == true || this.adding_area == true) {
                this.showCancelModal = true;
            } else {
                this.selected_zone = zone;

                this.selected_zone.group_delivery_days.forEach((group, index) => {
                    this.selected_zone.group_delivery_days[index].addTimeSlot = false;
                    this.selected_zone.group_delivery_days[index].availableTimes = this.deliveryTimes.filter(slot => {
                        return !group.times.some(item => item.alias === slot.alias);
                    });
                });
            }
        },

        addZone() {
            if(this.changes_made == true || this.adding_area == true) {
                this.showCancelModal = true;
            } else {
                this.selected_zone = null;
                this.new_zone.group_delivery_days[0].availableTimes = [...this.deliveryTimes];

                //push a new zone into the zones array
                this.zones.push(this.new_zone);
                //and set it to the selected zone
                let index = this.zones.findIndex(i => i.id == null);
                this.selected_zone = this.zones[index];
                this.changesMade();

                this.$nextTick(() => {
                    this.$refs.name.focus()
                })
            }
        },

        deleteZone() {
            this.showDeleteModal = true;
        },

        confirmDelete() {
            axios.delete(route('settings.delivery_zones.destroy', this.selected_zone.id))
            .then(response => {
                if (response.data.result === 'success') {
                    this.showDeleteModal = false;
                    this.selected_zone.areas.forEach(area => {
                        this.removeCircleFromTheMap(area.id);
                    });
                    this.resetSelection();
                    this.changes_made = false;
                    this.adding_area = false;

                    let index = this.zones.findIndex(i => i.id === this.selected_zone.id);

                    if (index !== -1) {
                        this.zones.splice(index, 1);
                    }

                    this.selected_zone = null;
                }
            })
        },

        addMarker() {
            this.adding_area = true;
            this.resetSelection();
            let center = this.$refs.googleMap.map.getCenter();

            const marker = {
                lat: center.lat(),
                lng: center.lng(),
            };

            this.markers.push({ position: marker });
        },

        getISODate(date) {
            return DateTime.fromISO(date).isValid ? date : DateTime.fromJSDate(date).toISODate();
        },

        getDeliveries() {
            //clear the current arrays, if set
            this.failed_requests = [];
            this.all_deliveries = [];

            //query the controller
            axios.get(route('delivery_zone.deliveries'), {
                    params: {
                        from: this.getISODate(this.dateRange[0]),
                        to: this.getISODate(this.dateRange[1]),
                        type: this.deliveryStatsType,
                    }
                })
                .then(response => {
                    if (response.data) {
                        let locations = response.data;
                        locations.forEach(location => {
                            //add the returned items to the array
                            let marker = {
                                lat: parseFloat(location.lat),
                                lng: parseFloat(location.lng),
                            };

                            if (this.deliveryStatsType == 'failed') {
                                this.failed_requests.push({
                                    position: marker,
                                    address: location.address,
                                    name: location.name,
                                    phone: location.phone,
                                    email: location.email,
                                    date: DateTime.fromISO(location.created_at).toLocaleString(DateTime.DATE_MED),
                                });
                            } else {
                                this.all_deliveries.push({ position: marker });
                            }
                        });
                    }
                })
        },

        clearDeliveries() {
            this.deliveryStatsType = null;
            this.failed_requests = [];
            this.all_deliveries = [];
        },

        deleteArea(id) {
            this.changesMade();
            this.resetSelection();
            let areas = this.selected_zone.areas;
            let item_to_delete = areas.findIndex(x => x.id == id);
            areas.splice(item_to_delete, 1);

            this.removeCircleFromTheMap(id);
        },

        changesMade() {
            this.changes_made = true;
        },

        drawCircle() {
            let radius = 5; //default 5km
            let marker = this.markers.pop();

            //draw circle in its place
            let area = {
                lat: marker.position.lat,
                lng: marker.position.lng,
                delivery_zone_id: this.selected_zone.id,
                radius: radius,
                // set a random id until the circle has its own
                id: Math.random().toString(36).slice(2, 7),
            }

            this.selected_zone.areas.push(area);
            this.all_areas = [];

            this.$nextTick(() => {
                this.setZones();
            })

            //reset the flags
            this.adding_area = false;
            this.changesMade();
        },

        selectCircle(id, index) {
            this.resetSelection();

            if (this.adding_area) return;

            if (this.selected_zone && this.all_areas[index].delivery_zone_id == this.selected_zone.id) {
                // check if clicked circle is existing, and in the same zone as the current zone
                this.selected_circle = id;
                this.setDraggableEditableStatus(index, true);

                return;
            }

            if (this.changes_made == true) {
                this.showCancelModal = true;

                return;
            }

            // circle from different zone selected, no changes pending. Update zone and circle selection.
            this.selected_circle = id;
            this.setDraggableEditableStatus(index, true);

            // find zone that the area belongs to
            let zone = this.all_areas[index].delivery_zone_id;
            let target = this.zones.findIndex(i => i.id == zone);

            // Only update selected_zone if a valid index returned and the zone has changed
            if (target != -1) {
                this.selectZone(this.zones[target]);
            }
        },

        removeCircleFromTheMap(id) {
            let index = this.getCircleIndexById(id)
            this.$refs.circle[index].circle.setMap(null);
        },

        setDraggableEditableStatus(index, status) {
            this.$refs.circle[index].circle.setDraggable(status);
            this.$refs.circle[index].circle.setEditable(status);
        },

        updateMarkerPos(event, index) {
            let lat = event.lat();
            let lng = event.lng();

            this.markers[index].position.lat = lat;
            this.markers[index].position.lng = lng;
        },

        updateRadius(id, value) {
            let index = this.getCircleIndexById(id);
            this.$refs.circle[index].circle.setRadius(Number(value) * 1000);
        },

        changeZoneColor(fillColor) {
            this.selected_zone.colour = fillColor;

            for (const index in this.all_areas) {
                const area = this.all_areas[index];

                if (area.delivery_zone_id === this.selected_zone.id) {
                    this.$refs.circle[index].circle.set('fillColor', fillColor);
                }
            }

            this.selected_zone.areas.forEach(item => {
                item.colour = fillColor;
            })

            this.changesMade();
        },

        circleCenterChanged(areaId, index) {
            let center = this.$refs.circle[index].circle.getCenter();
            let area = this.selected_zone.areas.find(item => item.id === areaId);
            area.lat = center.lat();
            area.lng = center.lng();
        },

        circleRadiusChanged(areaId, index) {
            if (this.changingAreaFromInput) return;

            let radius = this.$refs.circle[index].circle.getRadius();
            let circle = this.selected_zone.areas.find(item => item.id === areaId);
            //radius is in meters
            circle.radius = Math.round(radius / 100) / 10;
            this.changesMade();
        },

        resetSelection() {
            if (this.selected_circle) {
                let index = this.getCircleIndexById(this.selected_circle);
                this.setDraggableEditableStatus(index, false);
                this.selected_circle = null;
            }
        },

        getCircleIndexById(id) {
            return this.all_areas.findIndex(item => item.id === id)
        },

        handleSubmit() {
            let request = {
                data: this.selected_zone,
            };

            if (this.selected_zone.id) {
                request.url = route('settings.delivery_zones.update', { delivery_zone: this.selected_zone.id });
                request.method = 'put';
            } else {
                request.url = route('settings.delivery_zones.store');
                request.method = 'post';
            }

            axios(request).then(response => {
                if (response.data.result === 'success') {
                    let zone = response.data.zone;
                    this.selected_zone.id = zone.id;
                    let index = this.zones.findIndex(i => i.id === zone.id);
                    this.zones.splice(index, 1, zone);
                    this.all_areas = [];

                    this.$nextTick(() => {
                        this.setZones();
                        this.changes_made = false;
                        this.adding_area = false;
                        this.selected_zone = null;
                        this.errors = {};
                        this.selected_circle = null;
                        this.resetNewZone();
                    })
                }
            }).catch(error => {
                let errors = error.response.data.errors;

                for (const field in errors) {
                    this.errors[field] = errors[field][0];
                }
            })
        },

        cancel() {
            if (this.changes_made == true || this.adding_area == true) {
                this.showCancelModal = true;
            } else {
                this.markers = [];
                this.resetNewZone();
                this.selected_zone = null;
                this.resetSelection();
                this.changes_made = false;
                this.adding_area = false;
            }
        },

        confirmCancel() {
            axios(route('delivery_zones.index'))
                .then(response => {
                    if (response.data) {
                        this.zones = response.data.zones;
                        this.all_areas = [];
                    }

                    this.$nextTick(() => {
                        this.setZones();
                        this.markers = [];
                        this.resetNewZone();
                        this.selected_zone = null;
                        this.changes_made = false;
                        this.adding_area = false;
                        this.showCancelModal = false;
                        this.selected_circle = null;
                    });
                })
        },

        resetNewZone() {
            this.new_zone.active = true;
            this.new_zone.name = '';
            this.new_zone.location_id = 0;
            this.new_zone.colour = '#d41534';
            this.new_zone.limit = null;
            this.new_zone.fee = null;
            this.new_zone.free_delivery_available = false;
            this.new_zone.postal_codes = '';
            this.new_zone.delivery_days = [];
            this.new_zone.times = [];
            this.new_zone.areas = [];
        },

        addDeliverySlot() {
            if (this.availableDays.length === 0) {
                return false;
            }

            this.pushDeliveryGroupDays();
        },

        deleteDeliverySlot(index) {
            this.selected_zone.group_delivery_days.splice(index, 1);
            this.changeAvailableDays([1]);
        },

        pushDeliveryGroupDays() {
            let slot = {
                addTimeSlot: false,
                availableTimes: [...this.deliveryTimes],
                days: [],
                times: [],
            };
            this.selected_zone.group_delivery_days.push(slot);
            this.addNewSlot = true;
            this.changesMade();
        },

        changeAvailableDays(days = null) {
            this.addNewSlot = days.length > 0 ? false : true;
            this.changesMade();
        },

        showTimeSlots(group_day) {
            if (group_day.availableTimes.length === 0) {
                return false;
            }

            group_day.addTimeSlot = true;
            this.changesMade();
        },

        hideTimeSlots(group_day) {
            group_day.addTimeSlot = false;
            this.timeIndex = 0;
        },

        addTimeSlotToDeliveryZone(group_day) {
            group_day.addTimeSlot = false;
            let slot = group_day.availableTimes[this.timeIndex];
            slot.pivot = {
                delivery_zone_id: this.selected_zone.id,
                delivery_time_slot_id: slot.id,
                is_enable: true,
            };

            if (!group_day?.availableTimes) {
                group_day.availableTimes = [];
            }

            group_day.times.push(slot);
            group_day.availableTimes.splice(this.timeIndex, 1);
            this.timeIndex = 0;
        },

        deleteTimeSlotFromDeliveryZone(group_day, index, slot) {
            group_day.times.splice(index, 1);
            group_day.availableTimes.push(slot);
            group_day.availableTimes.sort((a, b) => a.sort - b.sort);
            this.changesMade();
        }
    },
}
</script>

<style scoped>
.zone-detail, .zone-list {
    height: 80vh;
    overflow-y: auto;
}

.zone-detail {
    display: flex;
    flex-direction: column;
    padding: 0.5rem;
    border-top-width: 1px;
}

.zone {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    height: 2rem;
    padding: 0 0.5rem;
    border-top-width: 1px;
    cursor: pointer;
}

.selected-zone, .zone:hover {
    background-color: rgba(217, 217, 217);
}

.zone-name {
    overflow: hidden;
    word-break: break-all;
    height: 1.5rem;
}

#zone-name:focus {
    z-index: 1;
}

.zone-color {
    min-width: 1.5rem;
    height: 1.5rem;
    border: 1px solid gray;
    border-radius: 0.25rem;
}

.delivery-area-input {
    padding-left: 0.25rem;
    padding-right: 0.25rem;
}
</style>

<style>
.delivery-toggle .toggle {
    width: 2.4rem;
    height: 1rem;
}

.delivery-toggle .toggle-handle {
    width: 1rem;
    height: 1rem;
}

.delivery-days {
    margin-bottom: 0.5rem;
}

.delivery-days .multiselect-tags {
    padding-left: 0.25rem;
}
.delivery-days .multiselect-tag {
    line-height: 1rem;
}

.delivery-days .multiselect-tag-remove {
    padding: 0.125rem;
}

.delivery-days .multiselect-caret,
.delivery-times-multiselect .multiselect-caret {
    margin-right: 0.5rem;
}

.delivery-times-multiselect .multiselect-single-label-text,
.delivery-times-multiselect .multiselect-option {
    font-size: 0.875rem;
}
</style>
